import { parseImportPrice } from "./articleImportModel";
import { getImportBarcodeError, normalizeImportBarcode } from "./articleImportBarcodes";

export const isImportRowSelected = (selection, id) => selection.all ? !selection.ids.has(id) : selection.ids.has(id);

export function toggleImportRowSelection(selection, id, selected) {
  return toggleImportRowsSelection(selection, [id], selected);
}

export function toggleImportRowsSelection(selection, rowIds, selected) {
  const ids = new Set(selection.ids);
  rowIds.forEach(id => {
    if (selected !== selection.all) ids.add(id);
    else ids.delete(id);
  });
  return { all: selection.all, ids };
}

// Assignments share immutable ID arrays. Detailed values are derived only for displayed
// subrows; an override is stored only when the user edits that organizational unit.
export async function assignImportCompanyUnit(rows, selection, unitId, availableIds, { signal, onProgress, remove = false } = {}) {
  const id = Number(unitId);
  if (!availableIds.has(id)) throw new Error("Njësia organizative nuk i përket listës aktuale të kompanisë.");
  const result = new Array(rows.length);
  const combinations = new Map();
  let changed = 0;
  for (let start = 0; start < rows.length; start += 5000) {
    if (signal?.aborted) throw new Error("Veprimi u anulua.");
    const end = Math.min(start + 5000, rows.length);
    for (let i = start; i < end; i++) {
      const row = rows[i];
      const existing = row.companyUnitIds || [];
      if (!isImportRowSelected(selection, row.id) || (remove ? !existing.includes(id) : existing.includes(id))) {
        result[i] = row;
        continue;
      }
      const key = existing.join(",");
      let ids = combinations.get(key);
      if (!ids) {
        ids = Object.freeze(remove ? existing.filter(unit => unit !== id) : [...existing, id]);
        combinations.set(key, ids);
      }
      result[i] = { ...row, companyUnitIds: ids };
      if (remove && row.unitDetails?.[id]) {
        const details = { ...row.unitDetails };
        delete details[id];
        result[i].unitDetails = details;
      }
      changed++;
    }
    onProgress?.(Math.round(end / rows.length * 100));
    if (end < rows.length) await new Promise(resolve => setTimeout(resolve, 0));
  }
  if (signal?.aborted) throw new Error("Veprimi u anulua.");
  return { rows: changed ? result : rows, changed };
}

export function addImportCompanyUnit(row, unitId) {
  const id = Number(unitId);
  if (!Number.isInteger(id) || id <= 0 || row.companyUnitIds?.includes(id)) return row;
  return { ...row, companyUnitIds: [...(row.companyUnitIds || []), id] };
}

export function removeImportCompanyUnit(row, unitId) {
  const overrides = { ...row.unitDetails };
  delete overrides[unitId];
  return { ...row, companyUnitIds: (row.companyUnitIds || []).filter(id => id !== unitId), unitDetails: overrides };
}

export function getImportCompanyUnitDetail(row, unitId) {
  const detail = {
    companyUnitId: Number(unitId), barcode: row.barcode, unitId: row.resolved.unit || "",
    salesType: 1, quantity: "1", salesPrice: row.salesPrice, active: true,
    ...row.unitDetails?.[unitId],
  };
  if (Number(detail.salesType) === 1) detail.quantity = "1";
  return detail;
}

export function updateImportCompanyUnitDetail(row, unitId, field, value) {
  if (!row.companyUnitIds?.includes(unitId)) return row;
  if (field === "quantity" && Number(getImportCompanyUnitDetail(row, unitId).salesType) === 1) return row;
  if (field === "barcode") value = normalizeImportBarcode(value);
  return { ...row, unitDetails: { ...row.unitDetails,
    [unitId]: { ...row.unitDetails?.[unitId], [field]: value,
      ...(field === "salesType" && Number(value) === 1 ? { quantity: "1" } : {}) },
  } };
}

export function getImportCompanyUnitErrors(row, detail, articleUnitIds) {
  const errors = {};
  const barcodeError = getImportBarcodeError(detail.barcode);
  if (barcodeError) errors.barcode = barcodeError;
  if (!articleUnitIds.has(Number(detail.unitId))) errors.unitId = "Zgjidhni njësinë matëse.";
  if (![1, 2].includes(Number(detail.salesType))) errors.salesType = "Zgjidhni pakicë ose shumicë.";
  const quantity = parseImportPrice(detail.quantity, null);
  if (quantity == null || quantity <= 0) errors.quantity = "Sasia në paketim duhet të jetë mbi zero.";
  if (parseImportPrice(detail.salesPrice, null) == null) errors.salesPrice = "Shënoni çmim zero ose pozitiv.";
  // An unresolved parent value is not silently cured by constructing a default detail.
  if (!row.unitDetails?.[detail.companyUnitId]?.barcode && row.errors.barcode) errors.barcode = row.errors.barcode;
  if (row.unitDetails?.[detail.companyUnitId]?.salesPrice === undefined && row.errors.salesPrice) errors.salesPrice = row.errors.salesPrice;
  return errors;
}
